"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@awesome.me/kit-c10ef98ed3/icons/classic/regular";
import { trackCtaClick } from "@/utils/commonEvents";
import { appendSessionUtmsToUrl } from "@/utils/utms";
import { getAssetUrl } from "@/utils/assetUrl";

const BOOK_APPOINTMENT_URL = "https://sleep.resmed.com/#insurance-check-section";

interface BookAppointmentSectionFemaleProps {
  resultOutcome: "likely" | "unlikely";
}

export default function BookAppointmentSectionFemale({
  resultOutcome,
}: BookAppointmentSectionFemaleProps) {
  const t = useTranslations("results-page.book-appointment-section");
  const bookingUrl = appendSessionUtmsToUrl(BOOK_APPOINTMENT_URL);
  const isHighRisk = resultOutcome === "likely";

  return (
    <section className="book-appointment-section-female">
      <h2
        className={`headline book-appointment-section-female-headline${!isHighRisk ? " book-appointment-section-female-headline-low-risk" : ""}`}
      >
        {t.rich(isHighRisk ? "silenced-symptoms" : "low-risk-headline", {
          highlight: (chunks) => (
            <span className="book-appointment-section-female-headline-highlight">
              {chunks}
            </span>
          ),
        })}
      </h2>

      {isHighRisk ? (
        <p className="book-appointment-section-female-bold-text">
          {t.rich("for-many-women", {
            bold: (chunks) => <strong>{chunks}</strong>,
            sup: (chunks) => <sup>{chunks}</sup>,
          })}
        </p>
      ) : (
        <p className="book-appointment-section-female-bold-text">
          {t("low-risk-body")}
        </p>
      )}

      <div className="book-appointment-section-female-video-container">
        {/* Fallback image — visible until the video plays over it */}
        <Image
          src={getAssetUrl(
            "/images/results/book_appointment_female_fallback.webp",
          )}
          alt={t("image-alt")}
          width={600}
          height={400}
          className="book-appointment-section-female-fallback"
          unoptimized
        />

        {/* Video layers on top — respects prefers-reduced-motion via CSS */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          className="book-appointment-section-female-video"
          poster={getAssetUrl(
            "/images/results/book_appointment_female_fallback.webp",
          )}
        >
          <source
            src={getAssetUrl("/images/results/book_appointment_female.mp4")}
            type="video/mp4"
          />
        </video>
      </div>

      {isHighRisk && (
        <p className="book-appointment-section-female-body">
          {t("taking-this-assessment")}
        </p>
      )}

      <div
        className={`book-appointment-section-female-buttons${
          isHighRisk ? " book-appointment-section-female-buttons-high-risk" : ""
        }`}
      >
        <Button
          variant="contained"
          color="secondary"
          href={bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="book-appointment-section-female-button"
          disableRipple
          onClick={() =>
            trackCtaClick({
              componentName: "sleep_assessment_pro",
              clickedArea: "Book now",
              clickedText: t("book-a-virtual-visit"),
              clickedUrl: bookingUrl,
            })
          }
        >
          {t("book-a-virtual-visit")}
          <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
        </Button>
      </div>
    </section>
  );
}
